import { useRef } from "react";
import type { KeyboardEvent } from "react";
import { Icon } from "../icons";
import type { IconName } from "../icons";
import { rovingIndex } from "./roving";

export type SegmentedOption<T extends string> = {
  value: T;
  label: string;
  icon?: IconName | null;
  title?: string;
  disabled?: boolean;
  action?: string;
};

export type SegmentedProps<T extends string> = {
  options: SegmentedOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  label: string;
  size?: "sm" | "md";
  iconOnly?: boolean;
  className?: string;
  dataFxmap?: string;
};

export function Segmented<T extends string>({ options, value, onChange, label, size = "md", iconOnly = false, className, dataFxmap }: SegmentedProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const enabled = options.map((option, position) => ({ option, position })).filter(a => !a.option.disabled);

    if (enabled.length === 0) return;

    const current = enabled.findIndex(a => a.position === index);
    const next = rovingIndex(event.key, current, enabled.length);

    if (next < 0) return;

    event.preventDefault();

    const target = enabled[next];

    refs.current[target.position]?.focus();
    onChange(target.option.value);
  };

  return (
    <div className={["fxmap-segmented", size === "sm" ? "fxmap-segmented-sm" : "", className ?? ""].filter(Boolean).join(" ")} role="radiogroup" aria-label={label} data-fxmap={dataFxmap}>
      {options.map((option, index) => {
        const checked = option.value === value;

        return (
          <button
            key={option.value}
            ref={element => {
              refs.current[index] = element;
            }}
            type="button"
            role="radio"
            className={["fxmap-segmented-item", checked ? "fxmap-segmented-item-on" : ""].filter(Boolean).join(" ")}
            aria-checked={checked}
            aria-label={iconOnly ? option.label : undefined}
            title={option.title ?? (iconOnly ? option.label : undefined)}
            tabIndex={checked || (value === null && index === 0) ? 0 : -1}
            disabled={option.disabled}
            data-fxmap-action={option.action}
            onClick={() => onChange(option.value)}
            onKeyDown={event => onKeyDown(event, index)}
          >
            {option.icon ? <Icon name={option.icon} /> : null}
            {iconOnly ? null : <span className="fxmap-btn-label">{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}
